/**
 * Recruitment pipeline templates — the "+ New pipeline" picker copy (SE-5487).
 *
 * This is FE-owned DISPLAY copy for the template picker: each option's label,
 * one-line description, and the stage names + glossary tooltips shown as a
 * preview on the card. The backend is the source of truth for the stages that
 * actually get seeded — the FE sends only the `KanbanPipelineTemplate` enum on
 * `CreateKanbanBoard` and the BE seeds the phases. The stage lists here MIRROR
 * that seed for the preview, and could in principle drift from it; that is an
 * accepted tradeoff (the preview is informational, the seed is authoritative).
 *
 * `CUSTOM` intentionally has zero stages: the BE seeds nothing, and the caller
 * opens the Stage Editor (SE-5488) so the empty board is not left unusable.
 * @module features/player-kanban/constants/pipelineTemplates
 */

import { KanbanPipelineTemplate } from "@/generated/graphql";

/** A single stage preview chip on a template card. */
export interface PipelineTemplateStage {
  /** Stage display name (mirrors the BE seed). */
  readonly name: string;
  /** Glossary tooltip explaining what the stage means. */
  readonly tooltip: string;
  /**
   * Whether this stage CLOSES the pipeline. The picker draws terminal stages as
   * outlined chips and the rest as filled grey ones (Figma 80:15758), which is
   * the card's only signal that a template ends somewhere rather than running
   * on forever.
   *
   * A flag rather than "the last one in the array" or a substring match on the
   * tooltip: both would silently mis-mark a template the moment its stage list
   * is edited, and this is display copy people do edit.
   */
  readonly isTerminal?: boolean;
}

/** One selectable template in the "+ New pipeline" picker. */
export interface PipelineTemplateOption {
  /** The enum sent to `CreateKanbanBoard` — the BE seeds stages from this. */
  readonly template: KanbanPipelineTemplate;
  /** Card title. */
  readonly label: string;
  /** One-line description under the title. */
  readonly description: string;
  /**
   * Stage preview (mirrors the BE seed). Empty for `CUSTOM`, which seeds no
   * stages and opens the Stage Editor instead.
   */
  readonly stages: readonly PipelineTemplateStage[];
  /**
   * Draws the "MOST USED" badge beside the title (Figma 80:15758). Editorial,
   * not measured: it marks the template we steer people toward, so it lives in
   * this display-copy module rather than being derived from usage data the FE
   * does not have.
   */
  readonly isMostUsed?: boolean;
}

/**
 * The template picker options, in display order. The three seeded templates
 * come first; `CUSTOM` (blank) is last.
 */
export const PIPELINE_TEMPLATE_OPTIONS: readonly PipelineTemplateOption[] = [
  {
    template: KanbanPipelineTemplate.NewAcquisitions,
    label: "New Player Acquisitions",
    description: "Track external targets from first sighting to signing.",
    isMostUsed: true,
    stages: [
      { name: "Discovery", tooltip: "Identified as a candidate." },
      { name: "Active Monitoring", tooltip: "Watching form/data." },
      { name: "Deep Scouting", tooltip: "Reports commissioned." },
      { name: "Evaluation", tooltip: "Fit/verdict decided internally." },
      { name: "Negotiation", tooltip: "Agent/club contact underway." },
      {
        name: "Decision",
        tooltip: "Final Signed or Passed — terminal.",
        isTerminal: true,
      },
    ],
  },
  {
    template: KanbanPipelineTemplate.AcademyPromotions,
    label: "Academy Promotions",
    description: "Move academy prospects toward the senior squad.",
    stages: [
      { name: "Identified", tooltip: "Academy prospect flagged." },
      { name: "Tracked", tooltip: "Monitored across the season." },
      { name: "Tested", tooltip: "Trained with a higher age group." },
      {
        name: "First Team Trial",
        tooltip: "Integrated with the senior squad.",
      },
      {
        name: "Promoted/Returned",
        tooltip: "Closed — terminal.",
        isTerminal: true,
      },
    ],
  },
  {
    template: KanbanPipelineTemplate.OutgoingTransfers,
    label: "Outgoing Transfers",
    description: "Manage players you intend to move on.",
    stages: [
      { name: "Listed", tooltip: "Internally flagged as surplus." },
      { name: "Open to Sale", tooltip: "Actively marketed." },
      { name: "In Discussions", tooltip: "Interest received, talking." },
      { name: "Offer Received", tooltip: "Concrete bid." },
      {
        name: "Sold/Withdrawn",
        tooltip: "Closed — terminal.",
        isTerminal: true,
      },
    ],
  },
  {
    template: KanbanPipelineTemplate.Custom,
    label: "Custom / blank",
    description: "Start empty and define your own stages.",
    stages: [],
  },
];

/**
 * The glossary line for a stage, resolved from the template that seeded it.
 *
 * `KanbanPhase` carries no description of its own — the only per-stage prose
 * this app has is the picker's glossary copy above, keyed by the board's
 * template and the stage NAME. Matching on the name means a renamed stage
 * loses its line (the copy described the seeded stage, and a rename is exactly
 * the case where the old description may no longer be true), and a stage the
 * user added themselves never had one. Both resolve to `""`, which callers
 * render as no description rather than as a placeholder.
 * @param template - The board's recruitment motion (null on legacy boards).
 * @param stageName - The stage's current name.
 * @returns The glossary line, or `""` when none applies.
 */
export const stageGlossaryFor = (
  template: KanbanPipelineTemplate | null | undefined,
  stageName: string
): string => {
  if (!template) return "";
  const option = PIPELINE_TEMPLATE_OPTIONS.find(
    candidate => candidate.template === template
  );
  const wanted = stageName.trim().toLowerCase();
  const stage = option?.stages.find(
    candidate => candidate.name.toLowerCase() === wanted
  );
  return stage?.tooltip ?? "";
};
